/**
 * @fileoverview Utilitário responsável por redimensionar e comprimir imagens antes do uso no frontend.
 */

/**
 * Comprime e redimensiona imagens antes de converter para base64.
 * Evita armazenar imagens de 1000x1000px no DOM quando exibidas em 32x32px.
 */

/**
 * Redimensiona e comprime uma imagem a partir de um File ou Blob.
 * @param {File|Blob} file - Arquivo de imagem
 * @param {Object} options
 * @param {number} options.maxWidth - Largura máxima (default: 200)
 * @param {number} options.maxHeight - Altura máxima (default: 200)
 * @param {number} options.quality - Qualidade JPEG 0-1 (default: 0.8)
 * @returns {Promise<string>} - Base64 string comprimida
 */
export const compressImageFile = (file, options = {}) => {
  const { maxWidth = 200, maxHeight = 200, quality = 0.8 } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      compressBase64Image(reader.result, { maxWidth, maxHeight, quality })
        .then(resolve)
        .catch(reject);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Comprime uma imagem já em base64.
 * @param {string} base64 - Data URI da imagem
 * @param {Object} options
 * @param {number} options.maxWidth - Largura máxima (default: 200)
 * @param {number} options.maxHeight - Altura máxima (default: 200)
 * @param {number} options.quality - Qualidade JPEG 0-1 (default: 0.8)
 * @returns {Promise<string>} - Base64 string comprimida
 */
export const compressBase64Image = (base64, options = {}) => {
  const { maxWidth = 200, maxHeight = 200, quality = 0.8 } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;

      // Calcula novas dimensões mantendo aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      const compressed = canvas.toDataURL("image/jpeg", quality);
      resolve(compressed);
    };
    img.onerror = reject;
    img.src = base64;
  });
};
